'use client'

import { getMonth, getYear, setMonth, setYear } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { FormLabel } from './form-label'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DISPLAY_DATE_FORMAT } from '@/constants/time'
import { cn } from '@/lib/utils'
import { dateInToronto, formatDate } from '@/utils/timeZoneUtils'

function formatDateDisplay(date: Date | undefined) {
  if (!date) return ''
  return formatDate(date, DISPLAY_DATE_FORMAT)
}

interface DatePickerProps {
  field: any // TanStack Form field
  label?: string | React.ReactNode
  placeholder?: string
  required?: boolean
  disableFutureDates?: boolean
  minDate?: Date
  startYear?: number
  endYear?: number
  id?: string
  name?: string
  className?: string
  showClearButton?: boolean
  disabled?: boolean
}

export default function DatePicker({
  field,
  label = 'Date',
  placeholder = 'Pick a date',
  required = false,
  disableFutureDates = false,
  minDate,
  startYear = getYear(new Date()) - 100,
  endYear = getYear(new Date()) + 10,
  id,
  name,
  className,
  showClearButton = false,
  disabled = false,
}: DatePickerProps) {
  const [open, setOpen] = useState(false)

  // Parse field value to Date
  const fieldDate = useMemo(() => {
    if (!field?.state?.value) return undefined
    if (field.state.value instanceof Date) return field.state.value
    if (typeof field.state.value === 'string') {
      const parsed = dateInToronto(field.state.value)
      return parsed.isValid() ? parsed.toDate() : undefined
    }
    return undefined
  }, [field?.state?.value])

  const [selectedDate, setSelectedDate] = useState<Date>(
    fieldDate || new Date(),
  )

  useEffect(() => {
    if (fieldDate) {
      setSelectedDate(fieldDate)
    }
  }, [fieldDate])

  // Generate months and years
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const years = Array.from(
    {
      length:
        (disableFutureDates ? getYear(new Date()) : endYear) - startYear + 1,
    },
    (_, i) => startYear + i,
  )

  const handleMonthChange = (month: string) => {
    const newDate = setMonth(selectedDate, months.indexOf(month))
    setSelectedDate(newDate)
  }

  const handleYearChange = (year: string) => {
    const newDate = setYear(selectedDate, parseInt(year))
    setSelectedDate(newDate)
  }

  const handleSelect = (selected: Date | undefined) => {
    if (selected) {
      const torontoDate = dateInToronto(selected).toDate()
      setSelectedDate(torontoDate)
      field.handleChange(torontoDate)
      setOpen(false)
    }
  }

  const handleClear = () => {
    field.handleChange(undefined)
    setSelectedDate(new Date())
  }

  // Date validation logic
  const isDateDisabled = useMemo(() => {
    if (!disableFutureDates && !minDate) return undefined
    return (d: Date) => {
      if (
        disableFutureDates &&
        dateInToronto(d).isAfter(dateInToronto(), 'day')
      )
        return true
      if (minDate && dateInToronto(d).isBefore(dateInToronto(minDate), 'day'))
        return true
      return false
    }
  }, [disableFutureDates, minDate])

  return (
    <div className={cn('w-full max-w-xs space-y-2', className)}>
      {label && (
        <FormLabel htmlFor={name} required={required}>
          {label}
        </FormLabel>
      )}

      <Popover
        open={!disabled && open}
        onOpenChange={disabled ? undefined : setOpen}
      >
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            variant="outline"
            id={id || name}
            disabled={disabled}
            className={cn(
              'w-full justify-start text-left font-normal',
              !fieldDate && 'text-muted-foreground',
            )}
            type="button"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {fieldDate ? formatDateDisplay(fieldDate) : placeholder}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="start">
          {/* Month and Year Selectors */}
          <div className="flex justify-between gap-2 p-3 border-b">
            <Select
              onValueChange={handleMonthChange}
              value={months[getMonth(selectedDate)]}
            >
              <SelectTrigger className="w-35 cursor-pointer">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                <ScrollArea className="h-80">
                  {months.map((month) => (
                    <SelectItem
                      key={month}
                      value={month}
                      className="cursor-pointer"
                    >
                      {month}
                    </SelectItem>
                  ))}
                </ScrollArea>
              </SelectContent>
            </Select>

            <Select
              onValueChange={handleYearChange}
              value={getYear(selectedDate).toString()}
            >
              <SelectTrigger className="cursor-pointer">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                <ScrollArea className="h-80">
                  {years.map((year) => (
                    <SelectItem
                      key={year}
                      value={year.toString()}
                      className="cursor-pointer"
                    >
                      {year}
                    </SelectItem>
                  ))}
                </ScrollArea>
              </SelectContent>
            </Select>
          </div>

          {/* Calendar */}
          <Calendar
            mode="single"
            selected={fieldDate}
            onSelect={handleSelect}
            disabled={isDateDisabled}
            month={selectedDate}
            onMonthChange={setSelectedDate}
            showOutsideDays={false}
            classNames={{
              month_caption: 'hidden',
              nav: 'hidden',
            }}
          />

          {/* Clear button */}
          {showClearButton && fieldDate && (
            <div className="p-3 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={handleClear}
                className="w-full"
                type="button"
              >
                Clear
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Error Messages */}
      {field.state.meta.isTouched && !field.state.meta.isValid ? (
        <p className="text-xs text-red-500">
          {field.state.meta.errors.join(', ')}
        </p>
      ) : null}

      {field.state.meta.isValidating ? (
        <p className="text-xs text-muted-foreground">Validating...</p>
      ) : null}
    </div>
  )
}
